/**
 * Unify ChargeItemDefinition model.
 *
 * Billable clinical-session HealthcareServices link to a ChargeItemDefinition
 * holding the default billing code and catalog base rate. ChargeItem.definitionUri
 * references ChargeItemDefinition.url at booking.
 */
import type { ChargeItemDefinition, Coding, HealthcareService } from '@medplum/fhirtypes';
import type { MedplumClient } from '@medplum/core';

import {
  CHARGE_ITEM_DEFINITION_CANONICAL_BASE,
  CHARGE_ITEM_DEFINITION_KEY_SYSTEM,
  CPT_CODE_SYSTEM,
  DEFAULT_SESSION_RATE_EXT,
  HEALTHCARE_SERVICE_CATALOG_URI_BASE,
} from '../constants';
import {
  getChargeItemDefinitionRef,
  getHealthcareServiceCatalogKey,
  getHealthcareServiceCategory,
} from './healthcare-service';

/** Returns CPT coding from HealthcareService.type (explicit CPT system only). */
export function getHealthcareServiceCptCoding(hs: HealthcareService): Coding | undefined {
  for (const concept of hs.type ?? []) {
    const coding = concept.coding?.find((c) => c.system === CPT_CODE_SYSTEM && c.code?.trim());
    if (coding?.code) return coding;
  }
  return undefined;
}

/** Returns stable catalog key from HS identifier, or derives one for custom rows. */
export function resolveHealthcareServiceCatalogKey(hs: HealthcareService): string | undefined {
  const existing = getHealthcareServiceCatalogKey(hs);
  if (existing) return existing;
  const cpt = getHealthcareServiceCptCoding(hs)?.code;
  if (cpt && hs.id) return `clinical-${cpt.toLowerCase()}-${hs.id}`;
  return undefined;
}

export function getChargeItemDefinitionUrl(catalogKey: string): string {
  return `${CHARGE_ITEM_DEFINITION_CANONICAL_BASE}/${catalogKey}`;
}

export function getHealthcareServiceCatalogUri(catalogKey: string): string {
  return `${HEALTHCARE_SERVICE_CATALOG_URI_BASE}/${catalogKey}`;
}

export function getChargeItemDefinitionKey(def: ChargeItemDefinition): string | undefined {
  return def.identifier?.find((id) => id.system === CHARGE_ITEM_DEFINITION_KEY_SYSTEM)?.value;
}

export function getBaseRateFromDefinition(def: ChargeItemDefinition): number | undefined {
  for (const group of def.propertyGroup ?? []) {
    for (const component of group.priceComponent ?? []) {
      if (component.type === 'base' && component.amount?.value != null) {
        return component.amount.value;
      }
    }
  }
  return undefined;
}

export type ChargeItemDefinitionCatalogStatus = 'active' | 'retired';

export interface ChargeItemDefinitionBillingMetadata {
  code: string;
  codeSystem: string;
  title: string;
  rate: number;
  status?: ChargeItemDefinitionCatalogStatus;
}

export function getChargeItemDefinitionCode(def: ChargeItemDefinition): string | undefined {
  return def.code?.coding?.find((coding) => coding.code?.trim())?.code?.trim();
}

/** Builds a billing definition from explicit billing metadata and a stable catalog key. */
export function buildChargeItemDefinition(
  catalogKey: string,
  billing: ChargeItemDefinitionBillingMetadata,
): ChargeItemDefinition {
  return {
    resourceType: 'ChargeItemDefinition',
    url: getChargeItemDefinitionUrl(catalogKey),
    identifier: [{ system: CHARGE_ITEM_DEFINITION_KEY_SYSTEM, value: catalogKey }],
    version: '1',
    status: billing.status === 'retired' ? 'retired' : 'active',
    title: billing.title,
    code: {
      coding: [{ system: billing.codeSystem, code: billing.code, display: billing.title }],
      text: billing.code,
    },
    derivedFromUri: [getHealthcareServiceCatalogUri(catalogKey)],
    propertyGroup: [
      {
        priceComponent: [
          {
            type: 'base',
            amount: { value: billing.rate, currency: 'USD' },
          },
        ],
      },
    ],
  };
}

function getDefaultSessionRateFromHs(hs: HealthcareService): number | undefined {
  const ext = hs.extension?.find((e) => e.url === DEFAULT_SESSION_RATE_EXT);
  return ext?.valueMoney?.value;
}

/**
 * Builds a ChargeItemDefinition from a clinical-session HealthcareService.
 * Returns undefined when the HS is not eligible (wrong category or missing CPT).
 */
export function buildChargeItemDefinitionFromHealthcareService(
  hs: HealthcareService,
  opts?: { rate?: number },
): ChargeItemDefinition | undefined {
  if (getHealthcareServiceCategory(hs) !== 'clinical-session') return undefined;

  const cptCoding = getHealthcareServiceCptCoding(hs);
  if (!cptCoding?.code) return undefined;

  const catalogKey = resolveHealthcareServiceCatalogKey(hs);
  if (!catalogKey) return undefined;

  const rate = opts?.rate ?? getDefaultSessionRateFromHs(hs) ?? 0;
  return buildChargeItemDefinition(catalogKey, {
    code: cptCoding.code,
    codeSystem: cptCoding.system ?? CPT_CODE_SYSTEM,
    title: cptCoding.display ?? hs.name ?? cptCoding.code,
    rate,
  });
}

export function shouldCreateChargeItemDefinitionForHealthcareService(hs: HealthcareService): boolean {
  return buildChargeItemDefinitionFromHealthcareService(hs) != null;
}

/** Search ChargeItemDefinition by catalog key identifier. */
export async function searchChargeItemDefinitionByCatalogKey(
  medplum: MedplumClient,
  catalogKey: string,
): Promise<ChargeItemDefinition | undefined> {
  return medplum.searchOne('ChargeItemDefinition', {
    identifier: `${CHARGE_ITEM_DEFINITION_KEY_SYSTEM}|${catalogKey}`,
  });
}

/** Creates or updates a billing definition from explicit metadata. */
export async function upsertChargeItemDefinition(
  medplum: MedplumClient,
  catalogKey: string,
  billing: ChargeItemDefinitionBillingMetadata,
): Promise<ChargeItemDefinition> {
  const built = buildChargeItemDefinition(catalogKey, billing);
  const existing = await searchChargeItemDefinitionByCatalogKey(medplum, catalogKey);
  if (existing?.id) {
    return medplum.updateResource({
      ...built,
      id: existing.id,
      meta: existing.meta,
    });
  }
  return medplum.createResource(built);
}

/**
 * Resolves the ChargeItemDefinition for a HealthcareService id.
 * Falls back to CPT match only when exactly one active definition matches.
 */
export async function resolveChargeItemDefinitionForHealthcareService(
  medplum: MedplumClient,
  healthcareServiceId: string,
): Promise<ChargeItemDefinition | undefined> {
  const hs = await medplum.readResource('HealthcareService', healthcareServiceId);
  const definitionRef = getChargeItemDefinitionRef(hs)?.reference;
  const definitionId = definitionRef?.match(/^ChargeItemDefinition\/([^/]+)$/)?.[1];
  if (definitionId) {
    return medplum.readResource('ChargeItemDefinition', definitionId);
  }

  const catalogKey = resolveHealthcareServiceCatalogKey(hs);
  if (catalogKey) {
    const byKey = await searchChargeItemDefinitionByCatalogKey(medplum, catalogKey);
    if (byKey) return byKey;
  }

  const cpt = getHealthcareServiceCptCoding(hs)?.code;
  const derivedUri = catalogKey ? getHealthcareServiceCatalogUri(catalogKey) : undefined;
  if (!cpt) return undefined;

  const candidates = await medplum.searchResources('ChargeItemDefinition', {
    code: cpt,
    status: 'active',
    _count: '10',
  });

  const filtered = candidates.filter((def) => {
    if (derivedUri && !def.derivedFromUri?.includes(derivedUri)) return false;
    return def.code?.coding?.some((c) => c.system === CPT_CODE_SYSTEM && c.code === cpt);
  });

  return filtered.length === 1 ? filtered[0] : undefined;
}

/** Creates or updates the paired ChargeItemDefinition for a clinical-session HS. */
export async function upsertChargeItemDefinitionForHealthcareService(
  medplum: MedplumClient,
  hs: HealthcareService,
  opts?: { rate?: number },
): Promise<ChargeItemDefinition | undefined> {
  const built = buildChargeItemDefinitionFromHealthcareService(hs, { rate: opts?.rate });
  if (!built) return undefined;

  const catalogKey = getChargeItemDefinitionKey(built) ?? resolveHealthcareServiceCatalogKey(hs);
  if (!catalogKey) return undefined;

  const coding = built.code?.coding?.[0];
  return upsertChargeItemDefinition(medplum, catalogKey, {
    code: coding?.code ?? '',
    codeSystem: coding?.system ?? CPT_CODE_SYSTEM,
    title: built.title ?? hs.name ?? coding?.code ?? '',
    rate: getBaseRateFromDefinition(built) ?? 0,
  });
}
